import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { activateOnEnter } from '@src/components/activateOnEnter';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Switch.css';

type NativeSwitchProps = Omit<
  ComponentPropsWithRef<'input'>,
  'style' | 'type' | 'role' | 'checked' | 'onChange' | 'children'
>;

export interface SwitchProps extends NativeSwitchProps {
  /** Controlled. */
  checked: boolean;
  /** State-first: callers want to know what it became, not the event. */
  onCheckedChange: (checked: boolean) => void;
  /** Sits beside the track, and is part of the click target. */
  label?: ReactNode;
  /** Quiet note below the label, beside the track. */
  hint?: ReactNode;
  disabled?: boolean;
  /** Parent id. The input, label and hint hang off it. */
  testId?: string;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  hint,
  disabled = false,
  className,
  id,
  onKeyDown,
  testId,
  ...rest
}: SwitchProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;

  const classes = ['switch'];
  if (disabled) {
    classes.push('switch--disabled');
  }
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')} {...testIdAttr(testId)}>
      <label className="switch__row">
        <input
          className="switch__input"
          id={inputId}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-labelledby={label ? `${inputId}-label` : undefined}
          aria-describedby={hint ? messageId : undefined}
          onChange={(event) => onCheckedChange(event.target.checked)}
          onKeyDown={(event) => {
            activateOnEnter(event);
            onKeyDown?.(event);
          }}
          {...testIdAttr(testIdFor(testId, 'control'))}
          {...rest}
        />

        <span className="switch__track" aria-hidden="true">
          <span className="switch__thumb" />
        </span>

        {(label || hint) && (
          <span className="switch__copy">
            {label && (
              <Typography
                as="span"
                role="label/control"
                className="switch__label"
                id={`${inputId}-label`}
                {...testIdAttr(testIdFor(testId, 'label'))}
              >
                {label}
              </Typography>
            )}
            {hint && (
              <Typography
                as="span"
                role="meta/helper"
                className="switch__message"
                id={messageId}
                {...testIdAttr(testIdFor(testId, 'hint'))}
              >
                {hint}
              </Typography>
            )}
          </span>
        )}
      </label>
    </div>
  );
}
